import React, { useState, useEffect, useRef, FC } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MenuItemProps, MenuSubProps, MenuProps } from "./MenuInterfaces";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

// Menu items that should show client/contractor dropdown on hover
const MENU_ITEMS_WITH_DROPDOWN = [
  "BOQ",
  "Work Order",
  "Work Execution",
  "RA Bills",
  "Reports",
];

interface MenuItemWithDropdownProps extends MenuItemProps {
  activeDropdown: string | null;
  setActiveDropdown: (text: string | null) => void;
}

const MenuItem: FC<MenuItemWithDropdownProps> = ({
  item,
  headActive,
  sidebarToggle,
  activeDropdown,
  setActiveDropdown,
}) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { text, link, newTab, badge, subMenu, icon } = item;
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get flowType from URL search params
  const searchParams = new URLSearchParams(search);
  const currentFlowType = searchParams.get("flowType");

  // Map menu items to their route patterns
  const getRoutePatterns = (menuText: string): string[] => {
    const patterns: { [key: string]: string[] } = {
      BOQ: ["work-boq"],
      "Work Request": ["work-request"],
      "Work Order": ["work-order", "view-work-orders", "Finalized-Work-Orders"],
      "Work Execution": ["work-execution"],
      "RA Bills": ["ra-bills"],
      Reports: ["work-order-reports"],
      "Payments & Receivables": [
        "view-receivables",
        "view-payments",
        "create-receivable",
        "create-payment",
        "pending-payment-approvals",
        "run-payments",
        "payment-analytics",
        "payments-analytics",
        "release-retention",
        "release-on-hold",
      ],
      "Client Receivables": [
        "view-receivables",
        "create-receivable",
        "pending-payment-approvals",
        "release-retention",
        "release-on-hold",
      ],
      "Contractor Payments": [
        "view-payments",
        "create-payment",
        "pending-payment-approvals",
        "run-payments",
        "payment-analytics",
        "payments-analytics",
        "release-retention",
        "release-on-hold",
      ],
    };
    return patterns[menuText] || [];
  };

  // Check if this menu item should show the client/contractor dropdown
  const shouldShowDropdown = MENU_ITEMS_WITH_DROPDOWN.includes(text);
  const showDropdown = shouldShowDropdown && activeDropdown === text;

  // Check if current page matches this menu item's routes to determine active flowType
  const isCurrentMenuItemActive = () => {
    const routePatterns = getRoutePatterns(text);
    if (routePatterns.length > 0) {
      const pathWithoutPrefix = pathname.replace(
        process.env.PUBLIC_URL || "",
        ""
      );
      return routePatterns.some((pattern) => {
        if (pattern === "work-order") {
          if (pathWithoutPrefix.includes("work-order-reports")) return false;
        }
        if (pathWithoutPrefix === pattern) return true;
        if (
          pathWithoutPrefix.startsWith(pattern + "/") ||
          pathWithoutPrefix.startsWith(pattern + "-")
        )
          return true;
        return pathWithoutPrefix.includes(pattern);
      });
    }
    return false;
  };

  const isMenuItemActive = isCurrentMenuItemActive();
  const isClientActive =
    isMenuItemActive && currentFlowType?.toLowerCase() === "client";
  const isContractorActive =
    isMenuItemActive && currentFlowType?.toLowerCase() === "contractor";

  // Recursively check if the current item or any of its sub-menu items is active
  const isActive = (item: any) => {
    const pathWithoutPrefix = pathname.replace(
      process.env.PUBLIC_URL || "",
      ""
    );
    const normalizedPath = pathWithoutPrefix.startsWith("/")
      ? pathWithoutPrefix.substring(1)
      : pathWithoutPrefix;

    // Check exact link match first (before Dashboard special case)
    if (item.link) {
      const normalizedLink = item.link.startsWith("/")
        ? item.link.substring(1)
        : item.link;
      const linkWithSlash = item.link.startsWith("/")
        ? item.link
        : "/" + item.link;
      const fullLink = process.env.PUBLIC_URL + linkWithSlash;

      // Normalize paths for comparison
      const normalizedPathname = pathname.replace(
        process.env.PUBLIC_URL || "",
        ""
      );
      const pathnameWithoutSlash = normalizedPathname.startsWith("/")
        ? normalizedPathname.substring(1)
        : normalizedPathname;

      // Check multiple variations of the link match
      if (
        fullLink === pathname ||
        pathname === process.env.PUBLIC_URL + item.link ||
        pathname === process.env.PUBLIC_URL + linkWithSlash ||
        pathWithoutPrefix === item.link ||
        pathWithoutPrefix === linkWithSlash ||
        normalizedPath === normalizedLink ||
        pathnameWithoutSlash === normalizedLink ||
        pathWithoutPrefix === "/" + item.link ||
        pathWithoutPrefix === item.link + "/" ||
        normalizedPathname.includes(normalizedLink) ||
        pathnameWithoutSlash.includes(normalizedLink)
      ) {
        return true;
      }
    }

    // Special handling for Dashboard - only match if it's actually a dashboard path
    if (item.text === "Dashboard") {
      const isDashboardPath =
        pathWithoutPrefix === "" ||
        pathWithoutPrefix === "/" ||
        pathWithoutPrefix === "dashboard" ||
        pathWithoutPrefix === "/dashboard" ||
        pathname === process.env.PUBLIC_URL + "/dashboard" ||
        pathname === process.env.PUBLIC_URL + "/" ||
        pathname === process.env.PUBLIC_URL;

      // Only return true if it's actually a dashboard path, otherwise return false
      return isDashboardPath;
    }

    // Check if pathname matches any route pattern for this menu item
    const routePatterns = getRoutePatterns(item.text);
    if (routePatterns.length > 0) {
      const pathWithoutPrefix = pathname.replace(
        process.env.PUBLIC_URL || "",
        ""
      );
      // Sort patterns by length (longest first) to match more specific patterns first
      const sortedPatterns = [...routePatterns].sort(
        (a, b) => b.length - a.length
      );

      // Check if path matches any pattern (exact match or starts with pattern followed by / or end of string)
      const pathMatches = sortedPatterns.some((pattern) => {
        // For "work-order" pattern, exclude "work-order-reports" FIRST (handled by Reports)
        if (pattern === "work-order") {
          if (pathWithoutPrefix.includes("work-order-reports")) return false;
        }

        // Exact match
        if (pathWithoutPrefix === pattern) return true;
        // Path starts with pattern followed by / or - (for routes like work-order-view)
        if (
          pathWithoutPrefix.startsWith(pattern + "/") ||
          pathWithoutPrefix.startsWith(pattern + "-")
        )
          return true;
        // For other patterns, check if path contains the pattern
        return pathWithoutPrefix.includes(pattern);
      });

      if (pathMatches) {
        // For "Client Receivables" and "Contractor Payments", also check flowType
        if (item.text === "Client Receivables") {
          return currentFlowType?.toLowerCase() === "client";
        }
        if (item.text === "Contractor Payments") {
          // Contractor Payments is active if flowType is Contractor or not specified (defaults to Contractor)
          return (
            !currentFlowType || currentFlowType?.toLowerCase() === "contractor"
          );
        }
        return true;
      }
    }

    // Check sub-menu items
    if (item.subMenu) {
      return item.subMenu.some((sub: any) => isActive(sub));
    }
    return false;
  };

  const linkClass = `nk-menu-link ${subMenu ? "nk-menu-toggle" : ""} ${
    shouldShowDropdown ? "nk-menu-toggle" : ""
  }`;

  const handleMouseEnter = () => {
    if (shouldShowDropdown) {
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      // Immediately close any other dropdown and open this one
      setActiveDropdown(text);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (shouldShowDropdown) {
      // Check if we're moving to another menu item with dropdown
      const relatedTarget = e.relatedTarget;
      let isMovingToAnotherMenuItem = false;

      // Check if relatedTarget is an HTMLElement and has closest method
      if (
        relatedTarget &&
        relatedTarget instanceof HTMLElement &&
        typeof relatedTarget.closest === "function"
      ) {
        isMovingToAnotherMenuItem = !!relatedTarget.closest(
          ".nk-menu-item.has-hover-dropdown"
        );
      }

      if (!isMovingToAnotherMenuItem) {
        // Only add delay if we're leaving the menu area entirely
        closeTimeoutRef.current = setTimeout(() => {
          setActiveDropdown(null);
        }, 150);
      } else {
        // Immediately close if moving to another menu item
        setActiveDropdown(null);
      }
    }
  };

  const handleDropdownMouseEnter = () => {
    // Cancel any pending close when mouse enters dropdown
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(text);
  };

  const handleDropdownMouseLeave = () => {
    if (shouldShowDropdown) {
      setActiveDropdown(null);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <li
      className={`nk-menu-item ${subMenu ? "has-sub" : ""} ${
        isActive(item) ? "active current-page" : ""
      } ${headActive ? "active current-page" : ""} ${
        shouldShowDropdown ? "has-hover-dropdown" : ""
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: "relative" }}
    >
      {newTab ? (
        <a
          href={link ? process.env.PUBLIC_URL + link : "#"}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {icon}
          {text}
          {badge && <span className="nk-menu-badge">{badge}</span>}
        </a>
      ) : subMenu ? (
        <React.Fragment>
          <a
            href="#toggle"
            className="nk-menu-link nk-menu-toggle"
            onClick={(ev) => ev.preventDefault()}
          >
            <span className="nk-menu-text">
              {icon}
              {text}
            </span>
          </a>
          <MenuSub
            sidebarToggle={sidebarToggle}
            subMenu={subMenu}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
          />
        </React.Fragment>
      ) : shouldShowDropdown ? (
        // For menu items with dropdown, don't make them clickable
        <a
          href="#"
          className={linkClass}
          onClick={(e) => e.preventDefault()}
          style={{ cursor: "pointer" }}
        >
          <span className="nk-menu-text">
            {icon}
            {text}
          </span>
          {badge && <span className="nk-menu-badge">{badge}</span>}
        </a>
      ) : (
        <Link
          className={linkClass}
          to={
            text === "Client Receivables"
              ? `${process.env.PUBLIC_URL}${link}?flowType=Client`
              : text === "Contractor Payments"
              ? `${process.env.PUBLIC_URL}${link}?flowType=Contractor`
              : text === "Work Request"
              ? `${process.env.PUBLIC_URL}${link}?flowType=Contractor`
              : process.env.PUBLIC_URL + link
          }
        >
          <span className="nk-menu-text">
            {icon}
            {text}
          </span>
          {badge && <span className="nk-menu-badge">{badge}</span>}
        </Link>
      )}

      {/* Client/Contractor Dropdown */}
      {shouldShowDropdown && showDropdown && (
        <ul
          className="nk-menu-sub"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            minWidth: "150px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "4px",
            padding: "8px 0",
            marginTop: "0",
            zIndex: 1000,
            listStyle: "none",
            opacity: 1,
            visibility: "visible",
            display: "block",
          }}
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          <li className={`nk-menu-item ${isClientActive ? "active" : ""}`}>
            <a
              className={`nk-menu-link ${isClientActive ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const targetPath =
                  text === "BOQ"
                    ? `/work-boq-approvals?flowType=Client`
                    : text === "Work Request"
                    ? `/work-request-list?flowType=Client`
                    : text === "Work Order"
                    ? `/view-work-orders?flowType=Client`
                    : text === "Work Execution"
                    ? `/work-execution-under-execution?flowType=Client`
                    : text === "RA Bills"
                    ? `/ra-bills-pending-approvals?flowType=Client`
                    : text === "Reports"
                    ? `/work-order-reports?flowType=Client`
                    : link || "#";
                // Use replace: true to update URL even on same page
                navigate(targetPath, { replace: true });
                setTimeout(() => setActiveDropdown(null), 100);
              }}
            >
              <span className="nk-menu-text">Client</span>
            </a>
          </li>
          <li className={`nk-menu-item ${isContractorActive ? "active" : ""}`}>
            <a
              className={`nk-menu-link ${isContractorActive ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const targetPath =
                  text === "BOQ"
                    ? `/work-boq-approvals?flowType=Contractor`
                    : text === "Work Request"
                    ? `/work-request-list?flowType=Contractor`
                    : text === "Work Order"
                    ? `/view-work-orders?flowType=Contractor`
                    : text === "Work Execution"
                    ? `/work-execution-under-execution?flowType=Contractor`
                    : text === "RA Bills"
                    ? `/ra-bills-pending-approvals?flowType=Contractor`
                    : text === "Reports"
                    ? `/work-order-reports?flowType=Contractor`
                    : link || "#";
                // Use replace: true to update URL even on same page
                navigate(targetPath, { replace: true });
                setTimeout(() => setActiveDropdown(null), 100);
              }}
            >
              <span className="nk-menu-text">Contractor</span>
            </a>
          </li>
        </ul>
      )}
    </li>
  );
};

const MenuSub: FC<
  MenuSubProps & {
    activeDropdown?: string | null;
    setActiveDropdown?: (text: string | null) => void;
  }
> = ({
  subMenu,
  sidebarToggle,
  activeDropdown = null,
  setActiveDropdown = () => {},
}) => (
  <ul className="nk-menu-sub">
    {subMenu.map((sub, index) => (
      <MenuItem
        key={index}
        item={sub}
        headActive={false}
        sidebarToggle={sidebarToggle}
        activeDropdown={activeDropdown}
        setActiveDropdown={setActiveDropdown}
      />
    ))}
  </ul>
);

const Menu: FC<MenuProps> = ({ sidebarToggle }) => {
  const {
    data: menu,
    loading,
    error,
  } = useSelector((state: RootState) => state.menu);
  const { pathname, search } = useLocation();
  const [activeHead, setActiveHead] = useState("");
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Map menu items to their route patterns (same as in MenuItem)
  const getRoutePatterns = (menuText: string): string[] => {
    const patterns: { [key: string]: string[] } = {
      BOQ: ["work-boq"],
      "Work Request": ["work-request"],
      "Work Order": ["work-order", "view-work-orders", "Finalized-Work-Orders"],
      "Work Execution": ["work-execution"],
      "RA Bills": ["ra-bills"],
      Reports: ["work-order-reports"],
      "Payments & Receivables": [
        "view-receivables",
        "view-payments",
        "create-receivable",
        "create-payment",
        "pending-payment-approvals",
        "run-payments",
        "payment-analytics",
        "payments-analytics",
        "release-retention",
        "release-on-hold",
      ],
      "Client Receivables": [
        "view-receivables",
        "create-receivable",
        "pending-payment-approvals",
        "release-retention",
        "release-on-hold",
      ],
      "Contractor Payments": [
        "view-payments",
        "create-payment",
        "pending-payment-approvals",
        "run-payments",
        "payment-analytics",
        "payments-analytics",
        "release-retention",
        "release-on-hold",
      ],
    };
    return patterns[menuText] || [];
  };

  useEffect(() => {
    const updateActiveHead = () => {
      const pathWithoutPrefix = pathname.replace(
        process.env.PUBLIC_URL || "",
        ""
      );

      // Sort menu items by pattern specificity (longer patterns first) to prioritize more specific matches
      // Also prioritize "Reports" over "Work Order" to avoid conflicts
      const sortedMenu = [...menu].sort((a: any, b: any) => {
        // Prioritize Reports over Work Order
        if (a.text === "Reports" && b.text === "Work Order") return -1;
        if (a.text === "Work Order" && b.text === "Reports") return 1;

        const patternsA = getRoutePatterns(a.text);
        const patternsB = getRoutePatterns(b.text);
        const maxLenA =
          patternsA.length > 0
            ? Math.max(...patternsA.map((p: string) => p.length))
            : 0;
        const maxLenB =
          patternsB.length > 0
            ? Math.max(...patternsB.map((p: string) => p.length))
            : 0;
        return maxLenB - maxLenA;
      });

      // Normalize pathWithoutPrefix to remove leading slash for pattern matching
      const normalizedPath = pathWithoutPrefix.startsWith("/")
        ? pathWithoutPrefix.substring(1)
        : pathWithoutPrefix;

      const activeItem: any = sortedMenu.find((item: any) => {
        // Skip Dashboard check first - check other items first
        if (item.text === "Dashboard") {
          return false; // Skip Dashboard, will check it later if no other match
        }

        // Check exact link match first
        if (
          item.link &&
          (item.link === pathWithoutPrefix ||
            item.link === normalizedPath ||
            process.env.PUBLIC_URL + item.link === pathname)
        ) {
          return true;
        }

        // Check route patterns
        const routePatterns = getRoutePatterns(item.text);
        if (routePatterns.length > 0) {
          // Sort patterns by length (longest first) to match more specific patterns first
          const sortedPatterns = [...routePatterns].sort(
            (a, b) => b.length - a.length
          );

          // Check if path matches any pattern
          if (
            sortedPatterns.some((pattern) => {
              // For "work-order" pattern, exclude "work-order-reports" FIRST (handled by Reports)
              if (pattern === "work-order") {
                if (
                  pathWithoutPrefix.includes("work-order-reports") ||
                  normalizedPath.includes("work-order-reports")
                )
                  return false;
              }

              // Exact match (with or without leading slash)
              if (pathWithoutPrefix === pattern || normalizedPath === pattern)
                return true;
              if (pathWithoutPrefix === "/" + pattern) return true;
              // Path starts with pattern followed by / or - (for routes like work-order-view)
              if (
                pathWithoutPrefix.startsWith("/" + pattern + "/") ||
                pathWithoutPrefix.startsWith("/" + pattern + "-") ||
                normalizedPath.startsWith(pattern + "/") ||
                normalizedPath.startsWith(pattern + "-")
              )
                return true;
              // For other patterns, check if path contains the pattern
              return (
                pathWithoutPrefix.includes(pattern) ||
                normalizedPath.includes(pattern)
              );
            })
          ) {
            return true;
          }
        }

        // Check sub-menu items
        if (item.subMenu) {
          // Get flowType from URL search params
          const searchParams = new URLSearchParams(search);
          const currentFlowType = searchParams.get("flowType");

          return item.subMenu.some((sub: any) => {
            // Check exact link match
            if (
              sub.link &&
              (sub.link === pathWithoutPrefix ||
                sub.link === normalizedPath ||
                process.env.PUBLIC_URL + sub.link === pathname)
            ) {
              // For "Client Receivables" and "Contractor Payments", also check flowType
              if (sub.text === "Client Receivables") {
                return currentFlowType?.toLowerCase() === "client";
              }
              if (sub.text === "Contractor Payments") {
                return (
                  !currentFlowType ||
                  currentFlowType?.toLowerCase() === "contractor"
                );
              }
              return true;
            }
            // Check if sub-menu item has route patterns
            const subRoutePatterns = getRoutePatterns(sub.text);
            if (subRoutePatterns.length > 0) {
              const sortedSubPatterns = [...subRoutePatterns].sort(
                (a, b) => b.length - a.length
              );
              const pathMatches = sortedSubPatterns.some((pattern) => {
                if (
                  pathWithoutPrefix === pattern ||
                  normalizedPath === pattern ||
                  pathWithoutPrefix === "/" + pattern
                )
                  return true;
                if (
                  pathWithoutPrefix.startsWith("/" + pattern + "/") ||
                  pathWithoutPrefix.startsWith("/" + pattern + "-") ||
                  normalizedPath.startsWith(pattern + "/") ||
                  normalizedPath.startsWith(pattern + "-")
                )
                  return true;
                return (
                  pathWithoutPrefix.includes(pattern) ||
                  normalizedPath.includes(pattern)
                );
              });

              if (pathMatches) {
                // For "Client Receivables" and "Contractor Payments", also check flowType
                if (sub.text === "Client Receivables") {
                  return currentFlowType?.toLowerCase() === "client";
                }
                if (sub.text === "Contractor Payments") {
                  return (
                    !currentFlowType ||
                    currentFlowType?.toLowerCase() === "contractor"
                  );
                }
                return true;
              }
            }
            return false;
          });
        }

        return false;
      });

      // Only check Dashboard if no other item matched
      if (!activeItem) {
        const dashboardItem = sortedMenu.find(
          (item: any) => item.text === "Dashboard"
        );
        if (dashboardItem) {
          const isDashboardPath =
            pathWithoutPrefix === "" ||
            pathWithoutPrefix === "/" ||
            normalizedPath === "dashboard" ||
            pathWithoutPrefix === "/dashboard" ||
            pathname === process.env.PUBLIC_URL + "/dashboard" ||
            pathname === process.env.PUBLIC_URL + "/" ||
            pathname === process.env.PUBLIC_URL;

          if (isDashboardPath) {
            setActiveHead("Dashboard");
            return;
          }
        }
      }

      // Set active head to found item, or Dashboard only if we're actually on dashboard
      setActiveHead(activeItem ? activeItem.text : "");
    };

    updateActiveHead();
    window.addEventListener("popstate", updateActiveHead);
    return () => window.removeEventListener("popstate", updateActiveHead);
  }, [pathname, search, menu]);

  return (
    <ul className="nk-menu nk-menu-main ui-s2">
      {menu.map((item: any, index: any) => (
        <MenuItem
          key={index}
          item={item}
          headActive={item.text === activeHead}
          sidebarToggle={sidebarToggle}
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
        />
      ))}
    </ul>
  );
};

export default Menu;
